import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { CalendarDays, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import {
  daysInMonth, describeDate, formatForField, isoParts, monthLabel, outOfBounds, parseTypedDate, toIso,
} from '../../utils/dateInput';
import { getTenantToday } from '../../utils/dateUtils';
import { isBusinessDayOn, onOrAfter } from '../../utils/businessDate';

/**
 * One date field for the platform (F-LR-020). Replaces <input type="date">, whose
 * segment-by-segment typing let "2026" become "0206" and post a trade in the year
 * 206. Here the person types the whole date the way they write it, or picks it
 * from a calendar, and the field reads it back in words ("Mon 2 Nov 2026") so a
 * wrong digit is visible before anything is saved. Impossible dates, implausible
 * years and dates outside the allowed range are refused with a reason, and the
 * field reports no value until the date is right, so a form's own "required"
 * check keeps its submit disabled.
 *
 * Value contract matches the native input: ISO YYYY-MM-DD in, ISO out, '' for none.
 */
export interface DateFieldProps {
  value: string;
  onChange: (iso: string) => void;
  id?: string;
  'aria-label'?: string;
  disabled?: boolean;
  /** Earliest allowed date, ISO. */
  min?: string;
  /** Latest allowed date, ISO. */
  max?: string;
  /** Words for the earliest date, e.g. "the facility's closing date". */
  minLabel?: string;
  /** Words for the latest date, e.g. "the business date". */
  maxLabel?: string;
  /**
   * The business date. The calendar marks it and offers a "Today" shortcut for it.
   * Without it the calendar marks the tenant's calendar day and offers no shortcut,
   * because a facility running on its own business date is not on the wall clock.
   */
  today?: string;
  /**
   * Market calendars the date must be a business day on (ADR 0009), e.g. an instrument's trade or
   * settlement calendars. A weekend or holiday is refused at the field with the next business day
   * and a one-click fix, and closed days are greyed out in the calendar. The server applies the
   * same rule on save.
   */
  calendars?: string[];
  /**
   * Why the field currently holds no date although something was typed: a weekend or holiday (with the next
   * business day), a date out of bounds, or an unreadable entry. A form that validates "required" can say
   * this instead. Called with null once the field is fine.
   */
  onProblem?: (problem: string | null, fix: { iso: string; label: string } | null) => void;
  /** Inline use in a toolbar: no read-back line; problems show on the border and in the tooltip. */
  compact?: boolean;
  /** 'light' on white surfaces (portco, alternatives). Defaults to the platform's dark theme. */
  tone?: Tone;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

/** Dark is the platform default; light matches the portco/alternatives surfaces. */
const PALETTES = {
  dark: {
    body: '#e5edf7', strong: '#edf4ff', muted: '#a8b6ca', faint: '#91a4bb', accent: '#67c6f4',
    line: '#29374b', lineStrong: '#3d526a', panel: '#0c1524', well: '#142033',
    bad: '#fca5a5', badLine: '#f87171', scheme: 'dark',
  },
  light: {
    body: '#1f2937', strong: '#0b1e3a', muted: '#4b5563', faint: '#6b7280', accent: '#2563eb',
    line: '#e5e7eb', lineStrong: '#d1d5db', panel: '#ffffff', well: '#ffffff',
    bad: '#b91c1c', badLine: '#dc2626', scheme: 'light',
  },
} as const;
type Tone = keyof typeof PALETTES;

/** Colours are CSS variables set on the wrapper, so the calendar popover inherits the tone. */
const C = {
  body: 'var(--df-body)', strong: 'var(--df-strong)', muted: 'var(--df-muted)', faint: 'var(--df-faint)',
  accent: 'var(--df-accent)', line: 'var(--df-line)', lineStrong: 'var(--df-line-strong)',
  panel: 'var(--df-panel)', well: 'var(--df-well)', bad: 'var(--df-bad)', badLine: 'var(--df-bad-line)',
};

const Wrap = styled.span<{ $tone: Tone }>`
  ${p => {
    const t = PALETTES[p.$tone];
    return css`
      --df-body: ${t.body}; --df-strong: ${t.strong}; --df-muted: ${t.muted}; --df-faint: ${t.faint};
      --df-accent: ${t.accent}; --df-line: ${t.line}; --df-line-strong: ${t.lineStrong};
      --df-panel: ${t.panel}; --df-well: ${t.well}; --df-bad: ${t.bad}; --df-bad-line: ${t.badLine};
      color-scheme: ${t.scheme};
    `;
  }}
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
`;

const Box = styled.span`
  position: relative;
  display: block;
`;

const TextInput = styled.input<{ $bad: boolean }>`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 0.5rem 2.1rem 0.5rem 0.6rem;
  border: 1px solid ${C.lineStrong};
  border-radius: 8px;
  background: ${C.well};
  color: ${C.body};
  font: inherit;
  font-size: 0.76rem;
  font-variant-numeric: tabular-nums;
  &:focus { outline: none; border-color: ${C.accent}; box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.18); }
  &::placeholder { color: ${C.faint}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  /* Screens style their own "label input" rules; keep room for the calendar button. */
  && { padding-right: 2.1rem; }
  ${p => p.$bad && css`
    && { border-color: ${C.badLine}; }
  `}
`;

const CalendarButton = styled.button`
  position: absolute;
  top: 50%;
  right: 0.3rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${C.muted};
  cursor: pointer;
  &:hover:not(:disabled) { color: ${C.strong}; background: rgba(103, 198, 244, 0.12); }
  &:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 1px; }
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

const Hint = styled.span<{ $bad: boolean }>`
  display: block;
  margin-top: 0.25rem;
  min-height: 1em;
  color: ${p => (p.$bad ? C.bad : C.muted)};
  font-size: 0.66rem;
  font-weight: 600;
  line-height: 1.3;
`;

const FixButton = styled.button`
  margin-left: 0.4rem;
  padding: 0 0.35rem;
  border: 1px solid ${C.badLine};
  border-radius: 4px;
  background: transparent;
  color: ${C.bad};
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  &:hover { color: ${C.strong}; border-color: ${C.lineStrong}; }
  &:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 1px; }
`;

const Popover = styled.div`
  position: absolute;
  z-index: 60;
  top: calc(100% + 4px);
  left: 0;
  width: 15.5rem;
  padding: 0.6rem;
  border: 1px solid ${C.lineStrong};
  border-radius: 10px;
  background: ${C.panel};
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
  color: ${C.body};
  font-size: 0.74rem;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
  margin-bottom: 0.4rem;
  strong { color: ${C.strong}; font-size: 0.78rem; }
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
  border: 1px solid ${C.line};
  border-radius: 6px;
  background: transparent;
  color: ${C.muted};
  cursor: pointer;
  &:hover { color: ${C.strong}; border-color: ${C.lineStrong}; }
  &:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 1px; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  span { color: ${C.faint}; font-size: 0.62rem; font-weight: 700; text-align: center; padding: 0.2rem 0; }
`;

const Day = styled.button<{ $selected: boolean; $today: boolean }>`
  height: 1.8rem;
  padding: 0;
  border: 1px solid ${p => (p.$today ? C.lineStrong : 'transparent')};
  border-radius: 6px;
  background: ${p => (p.$selected ? C.accent : 'transparent')};
  color: ${p => (p.$selected ? '#04121f' : C.body)};
  font: inherit;
  font-weight: ${p => (p.$selected ? 800 : 500)};
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  &:hover:not(:disabled) { background: ${p => (p.$selected ? C.accent : 'rgba(103, 198, 244, 0.14)')}; }
  &:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 1px; }
  &:disabled { color: #4b5b70; cursor: not-allowed; }
`;

const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.4rem;
  margin-top: 0.45rem;
  button {
    padding: 0.25rem 0.5rem;
    border: 1px solid ${C.line};
    border-radius: 6px;
    background: transparent;
    color: ${C.muted};
    font: inherit;
    font-size: 0.68rem;
    cursor: pointer;
  }
  button:hover:not(:disabled) { color: ${C.strong}; border-color: ${C.lineStrong}; }
  button:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const DateField: React.FC<DateFieldProps> = ({
  value, onChange, id, disabled, min, max, minLabel, maxLabel, today, calendars, compact, tone = 'dark', placeholder,
  className, style, onProblem, ...rest
}) => {
  const autoId = useId();
  const inputId = id || `date-${autoId}`;
  const hintId = `${inputId}-hint`;
  const [text, setText] = useState(() => formatForField(value));
  const [touched, setTouched] = useState(false);
  const [open, setOpen] = useState(false);
  const lastEmitted = useRef(value);
  const onProblemRef = useRef(onProblem);
  onProblemRef.current = onProblem;
  const wrapRef = useRef<HTMLSpanElement>(null);
  const todayIso = today || getTenantToday();

  // A value set from outside (a default, a reset, a server echo) replaces the text.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setText(formatForField(value));
    }
  }, [value]);

  const parsed = useMemo(() => parseTypedDate(text), [text]);
  const bound = parsed.kind === 'valid' ? outOfBounds(parsed.iso, min, max) : null;
  const onCalendars = !!calendars && calendars.length > 0;
  const closed = (iso: string) => onCalendars && !isBusinessDayOn(iso, calendars);
  const nextOpen = parsed.kind === 'valid' && !bound && closed(parsed.iso) ? onOrAfter(parsed.iso, calendars) : null;
  const problem = parsed.kind === 'invalid'
    ? parsed.reason
    : bound === 'before'
      ? `Earliest allowed is ${describeDate(min!)}${minLabel ? `, ${minLabel}` : ''}`
      : bound === 'after'
        ? `Latest allowed is ${describeDate(max!)}${maxLabel ? `, ${maxLabel}` : ''}`
        : nextOpen
          ? `${describeDate(parsed.kind === 'valid' ? parsed.iso : '')} is not a business day on ${calendars!.join(' + ')} — next is ${describeDate(nextOpen)}`
          : null;
  const showProblem = !!problem && (touched || text.trim().length >= 8);
  const reportProblem = onProblemRef.current;
  useEffect(() => {
    reportProblem?.(problem, nextOpen ? { iso: nextOpen, label: describeDate(nextOpen) } : null);
    // problem and nextOpen are derived from the typed text; the callback is read through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problem, nextOpen]);

  const emit = (iso: string) => {
    if (iso === lastEmitted.current) return;
    lastEmitted.current = iso;
    onChange(iso);
  };

  const onType = (next: string) => {
    setText(next);
    const r = parseTypedDate(next);
    if (r.kind === 'valid' && !outOfBounds(r.iso, min, max) && !closed(r.iso)) emit(r.iso);
    else emit('');
  };

  const onBlur = () => {
    setTouched(true);
    if (parsed.kind === 'valid' && !bound && !nextOpen) setText(formatForField(parsed.iso));
  };

  // Calendar month on open: the chosen date, else the earliest allowed, else today.
  const anchor = isoParts(parsed.kind === 'valid' ? parsed.iso : '') || isoParts(min || '') || isoParts(todayIso);
  const [view, setView] = useState(() => ({ year: anchor?.year || 2026, month: anchor?.month || 1 }));
  const openCalendar = () => {
    if (anchor) setView({ year: anchor.year, month: anchor.month });
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shift = (months: number) => setView(v => {
    const index = v.year * 12 + (v.month - 1) + months;
    return { year: Math.floor(index / 12), month: (index % 12) + 1 };
  });

  const pick = (iso: string) => {
    setText(formatForField(iso));
    setTouched(true);
    emit(iso);
    setOpen(false);
  };

  const firstWeekday = new Date(Date.UTC(view.year, view.month - 1, 1)).getUTCDay();
  const count = daysInMonth(view.year, view.month);
  const selectedIso = parsed.kind === 'valid' ? parsed.iso : '';
  const todayAllowed = !!today && !outOfBounds(todayIso, min, max) && !closed(todayIso);

  return (
    <Wrap ref={wrapRef} className={className} style={style} $tone={tone}>
      <Box>
        <TextInput
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder || 'mm/dd/yyyy'}
          value={text}
          disabled={disabled}
          aria-label={rest['aria-label']}
          aria-invalid={showProblem || undefined}
          aria-describedby={compact ? undefined : hintId}
          title={compact ? (showProblem ? problem! : parsed.kind === 'valid' ? describeDate(parsed.iso) : undefined) : undefined}
          $bad={showProblem}
          onChange={event => onType(event.target.value)}
          onBlur={onBlur}
          onKeyDown={event => { if (event.key === 'ArrowDown' && event.altKey) { event.preventDefault(); openCalendar(); } }}
        />
        <CalendarButton type="button" disabled={disabled} aria-label="Open calendar" aria-expanded={open}
          onClick={openCalendar}>
          <CalendarDays size={15} />
        </CalendarButton>
      </Box>
      {!compact && (
        <Hint id={hintId} $bad={showProblem} aria-live="polite">
          {showProblem ? problem : parsed.kind === 'valid' ? describeDate(parsed.iso) : ''}
          {showProblem && nextOpen && !disabled && (
            <FixButton type="button" onClick={() => pick(nextOpen)}>Use {describeDate(nextOpen)}</FixButton>
          )}
        </Hint>
      )}
      {open && (
        <Popover role="dialog" aria-label="Choose a date">
          <Head>
            <span style={{ display: 'flex', gap: 3 }}>
              <NavButton type="button" aria-label="Previous year" onClick={() => shift(-12)}><ChevronsLeft size={14} /></NavButton>
              <NavButton type="button" aria-label="Previous month" onClick={() => shift(-1)}><ChevronLeft size={14} /></NavButton>
            </span>
            <strong>{monthLabel(view.year, view.month)}</strong>
            <span style={{ display: 'flex', gap: 3 }}>
              <NavButton type="button" aria-label="Next month" onClick={() => shift(1)}><ChevronRight size={14} /></NavButton>
              <NavButton type="button" aria-label="Next year" onClick={() => shift(12)}><ChevronsRight size={14} /></NavButton>
            </span>
          </Head>
          <Grid>
            {WEEK.map(day => <span key={day}>{day}</span>)}
            {Array.from({ length: firstWeekday }).map((_, i) => <span key={`pad-${i}`} />)}
            {Array.from({ length: count }).map((_, i) => {
              const iso = toIso(view.year, view.month, i + 1);
              return (
                <Day key={iso} type="button" $selected={iso === selectedIso} $today={iso === todayIso}
                  disabled={!!outOfBounds(iso, min, max) || closed(iso)}
                  aria-label={closed(iso) ? `${describeDate(iso)}, not a business day` : describeDate(iso)}
                  aria-pressed={iso === selectedIso} onClick={() => pick(iso)}>
                  {i + 1}
                </Day>
              );
            })}
          </Grid>
          <Foot>
            {today ? <button type="button" disabled={!todayAllowed} onClick={() => pick(todayIso)}>Today ({formatForField(todayIso)})</button> : <span />}
            <button type="button" onClick={() => { setText(''); emit(''); setOpen(false); }}>Clear</button>
          </Foot>
        </Popover>
      )}
    </Wrap>
  );
};

export default DateField;
